import type { FC } from 'react';
import { Divider, ProductBadge } from '@yanolja-rnd/nol-design-system';
import { cn } from '@/app/_shared/utils/tw-cn';

/*
 * Price
 * 사용 영역: 국내숙소, 레저, 해외숙소
 * 타입 정보: Banner, List, Card
 */

export interface UnitPriceItem {
  top: {
    infoText?: string | null;
    discountRate?: string | null;
    originPrice?: string | null;
    originPriceNote?: string | null;
  };
  middle: {
    priceBadge?: string | null;
    discountPrice?: string | null;
    discountPriceUnit?: string | null;
    longTermDays?: string | null;
    closedInformation?: string | null;
    dailyPrice?: string | null;
  };
  bottom: { couponText?: string | null };
}

export interface UnitPriceProps {
  prices: UnitPriceItem[];
}

export const UnitPrice: FC<UnitPriceProps> = ({ prices = [] }) => {
  if (!prices?.length) return null;

  return (
    <div className="flex flex-1 flex-col items-end justify-end pt-4">
      {prices.map((price, index) => (
        <UnitPriceItem key={index} {...price} />
      ))}
    </div>
  );
};

export const UnitCardPrice: FC<UnitPriceProps> = ({ prices = [] }) => {
  if (!prices?.length) return null;

  return (
    <div className="flex flex-1 flex-col items-end justify-end">
      {prices.map((price, index) => (
        <UnitCardPriceItem key={index} {...price} />
      ))}
    </div>
  );
};

const MAX_PRICE_LENGTH = 11; // 최대 가격 길이

/**
 * 상품유닛 가격정보
 * @see {@link https://yanolja.atlassian.net/wiki/spaces/~jusong.lee/pages/491947083/PLP+SRP#FE-API-Response---%EC%83%81%ED%92%88-%EC%9C%A0%EB%8B%9B}
 * */
const UnitPriceItem: FC<UnitPriceItem> = ({ top, middle, bottom }) => {
  const showTopVerticalDivideLine = top?.infoText && (top?.discountRate || top?.originPrice || top?.originPriceNote);

  const showTopSection = top?.infoText || top?.discountRate || top?.originPrice || top?.originPriceNote;
  const showMiddleSection =
    middle?.priceBadge ||
    middle?.discountPrice ||
    middle?.discountPriceUnit ||
    middle?.longTermDays ||
    middle?.closedInformation ||
    middle?.dailyPrice;

  const showBottomSection = bottom?.couponText;
  return (
    <div>
      {/* 상단 영역*/}
      {!!showTopSection && (
        <div className="mt-6 flex flex-wrap items-center justify-end">
          {!!top.infoText && <span className="text-text-neutral-main typography-body-12-regular">{top.infoText}</span>}
          {!!showTopVerticalDivideLine && <Divider type="vertical" className="ml-4 h-8" />}
          {!!top.discountRate && (
            <span className="ml-4 text-text-neutral-main typography-body-12-regular">{top.discountRate}</span>
          )}
          {/* originalPrice와 originalPriceNote 는 서로 배타적인 관계*/}
          {!!top.originPrice && (
            <span className="ml-2 text-text-neutral-sub line-through typography-body-12-regular">
              {top.originPrice}
            </span>
          )}
          {!!top.originPriceNote && (
            <span className="ml-4 text-text-neutral-sub typography-body-12-regular">{top.originPriceNote}</span>
          )}
        </div>
      )}
      {/* 중간영역 */}
      {/* 뱃지 비즈니스가이드 참고 @see {@link https://screen.yanolja.in/T4QMpMYCV3EUGcm0} */}
      {!!showMiddleSection && (
        <>
          <div className="mb-2 mt-4 flex shrink-0 grow-0 flex-wrap items-center justify-end gap-4">
            {!!middle?.priceBadge && (
              <ProductBadge type="filled_basic" className="shrink-0 grow-0" color="text-neutral-main" size="medium">
                {middle?.priceBadge}
              </ProductBadge>
            )}
            {/* 가격 */}
            {!!(middle.discountPrice || middle.discountPriceUnit || middle.longTermDays) && (
              <div className="inline-flex items-center">
                {!!middle?.discountPrice && (
                  <span className="shrink-0 grow-0 text-text-neutral-main typography-subtitle-18-bold">
                    {middle?.discountPrice}
                  </span>
                )}
                {!!middle?.discountPriceUnit && (
                  <span className="shrink-0 grow-0 text-text-neutral-main typography-subtitle-16-bold">
                    {middle?.discountPriceUnit}
                  </span>
                )}
                {!!middle?.longTermDays && (
                  <span className="shrink-0 grow-0 text-text-neutral-main typography-body-14-bold">
                    {middle?.longTermDays}
                  </span>
                )}
              </div>
            )}
            {/* 가격 대체문구 */}
            {!!middle?.closedInformation && (
              <p className="shrink-0 grow-0 text-text-neutral-sub typography-subtitle-16-bold">
                {middle?.closedInformation}
              </p>
            )}
          </div>
          <div className="flex items-center justify-end">
            {!!middle?.dailyPrice && (
              <span className="text-text-neutral-main typography-body-12-regular">{middle?.dailyPrice}</span>
            )}
          </div>
        </>
      )}
      {/* 하단영역 */}
      {!!showBottomSection && (
        <div className="mt-2 flex justify-end">
          {/* 쿠폰 마감 임박 */}
          {!!bottom?.couponText && (
            <p className="text-text-warning-main typography-caption-11-bold">{bottom?.couponText}</p>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * 상품유닛 가격정보
 * @see {@link https://yanolja.atlassian.net/wiki/spaces/~jusong.lee/pages/491947083/PLP+SRP#FE-API-Response---%EC%83%81%ED%92%88-%EC%9C%A0%EB%8B%9B}
 * */
const UnitCardPriceItem: FC<UnitPriceItem> = ({ top, middle }) => {
  const { infoText } = top;
  const { discountPrice, discountPriceUnit, priceBadge, longTermDays, closedInformation, dailyPrice } = middle;

  const showDiscountPrice = discountPrice || discountPriceUnit || longTermDays || dailyPrice;
  const isMaxPriceLength = (!closedInformation && discountPrice && discountPrice?.length > MAX_PRICE_LENGTH) || false;

  return (
    <div className="mt-2 flex flex-col flex-wrap items-center justify-end gap-4">
      <div className="flex flex-col">
        <div className={cn(isMaxPriceLength ? 'flex flex-col' : 'flex flex-row items-center gap-4')}>
          <div className={cn(isMaxPriceLength && 'justify-end', 'flex items-center gap-4')}>
            {!!infoText && <p className="text-text-neutral-main typography-body-12-regular">{infoText}</p>}
            {!!priceBadge && (
              <ProductBadge
                type="filled_basic"
                className="ml-4 shrink-0 grow-0 typography-label-10-bold pc:py-0"
                color="text-neutral-main"
                size="small">
                {priceBadge}
              </ProductBadge>
            )}
          </div>
          {closedInformation ? (
            <span className="flex shrink-0 grow-0 items-center text-text-neutral-sub typography-body-12-bold">
              {closedInformation}
            </span>
          ) : (
            <div className="flex items-center justify-end">
              {!!discountPrice && (
                <span className="shrink-0 grow-0 text-text-neutral-main typography-body-14-bold">{discountPrice}</span>
              )}
              {!!discountPriceUnit && (
                <span className="shrink-0 grow-0 text-text-neutral-main typography-body-12-bold">
                  {discountPriceUnit}
                </span>
              )}
              {!!longTermDays && (
                <span className="shrink-0 grow-0 text-text-neutral-main typography-body-12-bold">{longTermDays}</span>
              )}
            </div>
          )}
        </div>
        {showDiscountPrice && (
          <div className="flex w-full justify-end">
            {!!dailyPrice && <span className="text-text-neutral-main typography-body-12-regular">{dailyPrice}</span>}
          </div>
        )}
      </div>
    </div>
  );
};
